import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getImageUrl } from './utils/mediaUtils';
import { getAPIBase } from './config';
import { ArrowLeft, Clock, Calendar, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';

const STATIC_PALC_DATA = {
  'support': {
    title: 'Family Support Circles',
    subtitle: 'Building Connections & Peer Support',
    description: 'Monthly gatherings designed to connect families, share insights, and build a supportive parent network that strengthens our school community.',
    color: '#7c3aed',
    colorLight: '#ede9fe',
    icon: 'fa-people-group',
    images: [],
    date: 'Monthly',
    frequency: 'Every 2nd Saturday',
    features: [
      {
        icon: 'fa-users',
        title: 'Peer Connection',
        detail: 'Meet other parents, share experiences, and build meaningful relationships with families in our community.'
      },
      {
        icon: 'fa-comments',
        title: 'Open Discussions',
        detail: 'Facilitated conversations on parenting challenges, child development, and school life experiences.'
      },
      {
        icon: 'fa-lightbulb',
        title: 'Shared Insights',
        detail: 'Learn from experienced parents and educators about effective parenting strategies and support systems.'
      },
      {
        icon: 'fa-heart',
        title: 'Emotional Support',
        detail: 'A safe, welcoming space where parents can share concerns and receive encouragement from peers.'
      }
    ],
    highlights: [
      '50+ families participate in monthly gatherings',
      'Expert facilitators guide meaningful discussions',
      'Refreshments and childcare provided',
      'Flexible scheduling for working parents'
    ],
    content: 'Family Support Circles are the heart of our Parents Learning Community. These monthly gatherings create a safe, welcoming space where parents can connect with others, share their experiences, and receive support from peers who understand the joys and challenges of parenting. Our expert facilitators guide discussions on topics ranging from managing school transitions to supporting your child\'s learning at home. Whether you\'re a new parent or a seasoned one, these circles offer valuable connections and practical insights that strengthen both your family and our school community.'
  },
  'workshops': {
    title: 'Learning Workshops',
    subtitle: 'Empowering Parents Through Education',
    description: 'Interactive sessions on learning strategies, child wellbeing, and student development at home. Designed to equip parents with practical tools and knowledge.',
    color: '#06b6d4',
    colorLight: '#cffafe',
    icon: 'fa-book-open',
    images: [],
    date: 'Quarterly',
    frequency: 'Every 3 months',
    features: [
      {
        icon: 'fa-brain',
        title: 'Learning Strategies',
        detail: 'Discover effective techniques to support your child\'s academic growth and foster a love of learning at home.'
      },
      {
        icon: 'fa-heart-pulse',
        title: 'Child Wellbeing',
        detail: 'Learn about emotional intelligence, stress management, and creating a healthy home environment for your child.'
      },
      {
        icon: 'fa-chart-line',
        title: 'Progress Tracking',
        detail: 'Understand how to monitor your child\'s development and communicate effectively with teachers about progress.'
      },
      {
        icon: 'fa-graduation-cap',
        title: 'Educational Excellence',
        detail: 'Expert-led sessions on curriculum, learning goals, and how to support your child\'s academic journey.'
      }
    ],
    highlights: [
      '8+ workshops offered annually',
      'Expert speakers from education and psychology fields',
      'Practical takeaways and resource materials',
      'Q&A sessions with educators and specialists'
    ],
    content: 'Our Learning Workshops are designed to empower parents with the knowledge and tools needed to support their children\'s academic and personal development. Led by experienced educators, psychologists, and specialists, these interactive sessions cover topics such as effective study habits, managing test anxiety, supporting different learning styles, and fostering emotional intelligence. Each workshop combines expert insights with practical strategies that parents can immediately apply at home.'
  },
  'community': {
    title: 'Community Collaboration',
    subtitle: 'Parents & School Working Together',
    description: 'Joint initiatives that connect parents with school programs, events, and service projects. Creating meaningful partnerships that benefit our entire community.',
    color: '#f59e0b',
    colorLight: '#fef3c7',
    icon: 'fa-handshake',
    images: [],
    date: 'Ongoing',
    frequency: 'Throughout the year',
    features: [
      {
        icon: 'fa-calendar-check',
        title: 'Event Coordination',
        detail: 'Parents volunteer to organize and lead school events, celebrations, and community gatherings.'
      },
      {
        icon: 'fa-hands-helping',
        title: 'Service Projects',
        detail: 'Participate in community service initiatives that make a positive impact on our local neighborhood.'
      },
      {
        icon: 'fa-school',
        title: 'School Programs',
        detail: 'Support classroom activities, field trips, and enrichment programs that enhance student learning.'
      },
      {
        icon: 'fa-star',
        title: 'Leadership Roles',
        detail: 'Take on leadership positions in parent committees and contribute to school decision-making.'
      }
    ],
    highlights: [
      '200+ parent volunteers annually',
      '15+ community service projects per year',
      'Parent-led committees and initiatives',
      'Direct impact on school culture and community'
    ],
    content: 'Community Collaboration brings parents and school together in meaningful ways. Through volunteer opportunities, service projects, and collaborative initiatives, parents become active partners in creating a vibrant school community. Whether organizing school events, leading service projects, or supporting classroom activities, parent volunteers strengthen our school culture and demonstrate to students the value of community engagement.'
  }
};

function PaLCDetailPage({ palcId, onNavigate, page }) {
  const [activeImageSlide, setActiveImageSlide] = useState(0);
  const [dynamicSectionData, setDynamicSectionData] = useState(null);
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_map');
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return {};
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [palcId]);

  // Fetch dynamic sections and media map for Parents
  useEffect(() => {
    let isMounted = true;
    const fetchParentsData = async () => {
      const apiBase = getAPIBase();
      try {
        const [secRes, medRes] = await Promise.all([
          fetch(`${apiBase}/homepage/cms/sections?published=true&page=parents&_t=${Date.now()}`, {
            credentials: 'omit'
          }),
          fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
            credentials: 'omit'
          })
        ]);

        if (!isMounted) return;

        if (medRes.ok) {
          const medJson = await medRes.json();
          if (medJson.ok && Array.isArray(medJson.data)) {
            const map = {};
            const origin = 'https://permataharapansecondary.sch.id';
            medJson.data.forEach(m => {
              const mId = m.id || m.media_id;
              let mUrl = m.url || m.file_path;
              if (mUrl && !mUrl.startsWith('http://') && !mUrl.startsWith('https://')) {
                mUrl = `${origin}${mUrl.startsWith('/') ? '' : '/'}${mUrl}`;
              }
              if (mId && mUrl) {
                map[mId] = mUrl;
                map[String(mId)] = mUrl;
                const num = Number(mId);
                if (!isNaN(num)) map[num] = mUrl;
              }
            });
            setMediaMap(map);
            try {
              sessionStorage.setItem('cms_media_map', JSON.stringify(map));
            } catch (e) {}
          }
        }

        if (secRes.ok) {
          const secJson = await secRes.json();
          const sections = Array.isArray(secJson.data) ? secJson.data : Object.values(secJson.data || {});
          if (isMounted) {
            setDynamicSectionData(sections);
          }
        }
      } catch (e) {
        console.warn('[PaLCDetailPage] Fetch error, using fallback:', e);
      }
    };

    fetchParentsData();

    const handleApprovalComplete = () => {
      fetchParentsData();
    };
    window.addEventListener('approvalCompleted', handleApprovalComplete);
    window.addEventListener('cmsContentUpdated', handleApprovalComplete);

    return () => {
      isMounted = false;
      window.removeEventListener('approvalCompleted', handleApprovalComplete);
      window.removeEventListener('cmsContentUpdated', handleApprovalComplete);
    };
  }, [palcId]);

  // Resolve media URL helper
  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;

    if (mediaMap) {
      if (mediaMap[str]) return mediaMap[str];
      const num = Number(str);
      if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    }

    const resolved = getImageUrl(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      return `https://permataharapansecondary.sch.id${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      return `https://permataharapansecondary.sch.id${str}`;
    }
    if (/^\d+$/.test(str)) {
      return null;
    }
    return str;
  }, [mediaMap]);

  // Find program dynamically from section data
  const dynamicProgram = useMemo(() => {
    const rawKey = String(palcId || '').toLowerCase().trim();
    const cleanKey = rawKey.replace(/^palc-/, '');

    // 1. Check fresh dynamicSectionData from database API
    if (dynamicSectionData && Array.isArray(dynamicSectionData)) {
      for (const section of dynamicSectionData) {
        if (section.section_type !== 'parents-palc' && section.section_type !== 'palc' && section.section_type !== 'parents-learning-community') {
          continue;
        }

        let content = section.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = section.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }
        const en = content?.en || content || {};
        const items = Array.isArray(en.items) ? en.items : (Array.isArray(en.parents_learning_community) ? en.parents_learning_community : []);

        for (let idx = 0; idx < items.length; idx++) {
          const it = items[idx];
          if (!it) continue;
          const itId = String(it.id || '').toLowerCase().replace(/^palc-/, '');
          const itSlug = String(it.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

          if (itId === cleanKey || itId === rawKey || itSlug === cleanKey || itSlug === rawKey || `prog-${idx + 1}` === cleanKey) {
            return it;
          }
        }
      }
    }

    // 2. Check passed page prop
    const pageItems = page?.parents_learning_community || page?.items || [];
    for (let idx = 0; idx < pageItems.length; idx++) {
      const it = pageItems[idx];
      if (!it) continue;
      const itId = String(it.id || '').toLowerCase().replace(/^palc-/, '');
      const itSlug = String(it.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      if (itId === cleanKey || itId === rawKey || itSlug === cleanKey || itSlug === rawKey || `prog-${idx + 1}` === cleanKey) {
        return it;
      }
    }

    // 3. Fallback to cached parents page
    try {
      const cached = sessionStorage.getItem('cms_page_parents');
      if (cached) {
        const parsed = JSON.parse(cached);
        const cachedItems = parsed.parents_learning_community || parsed.items || [];
        for (let idx = 0; idx < cachedItems.length; idx++) {
          const it = cachedItems[idx];
          if (!it) continue;
          const itId = String(it.id || '').toLowerCase().replace(/^palc-/, '');
          const itSlug = String(it.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          if (itId === cleanKey || itId === rawKey || itSlug === cleanKey || itSlug === rawKey) {
            return it;
          }
        }
      }
    } catch (e) {}

    return null;
  }, [palcId, page, dynamicSectionData]);

  // Construct final palc object merging dynamic or static fallback
  const palc = useMemo(() => {
    const rawKey = String(palcId || '').toLowerCase().trim();
    const cleanKey = rawKey.replace(/^palc-/, '');

    if (dynamicProgram) {
      // Resolve slideshow images
      const dynamicImagesList = (() => {
        let rawImgs = dynamicProgram.images;
        if (typeof rawImgs === 'string') {
          try {
            const parsed = JSON.parse(rawImgs);
            if (Array.isArray(parsed)) rawImgs = parsed;
          } catch (e) {}
        }
        if (Array.isArray(rawImgs) && rawImgs.length > 0) {
          const list = rawImgs.map(img => resolveImage(img)).filter(Boolean);
          if (list.length > 0) return list;
        }
        if (dynamicProgram.image) {
          const single = resolveImage(dynamicProgram.image);
          if (single) return [single];
        }
        return [];
      })();

      // Resolve features
      let rawFeatures = dynamicProgram.features;
      if (typeof rawFeatures === 'string') {
        try {
          rawFeatures = JSON.parse(rawFeatures);
        } catch (e) {
          rawFeatures = [];
        }
      }
      const features = Array.isArray(rawFeatures)
        ? rawFeatures.filter(f => f && (f.title || f.detail))
        : [];

      // Resolve highlights
      let rawHighlights = dynamicProgram.highlights;
      if (typeof rawHighlights === 'string') {
        try {
          const parsed = JSON.parse(rawHighlights);
          if (Array.isArray(parsed)) rawHighlights = parsed;
        } catch (e) {}
      }
      const highlights = Array.isArray(rawHighlights)
        ? rawHighlights.filter(Boolean)
        : (typeof rawHighlights === 'string' && rawHighlights ? [rawHighlights] : []);

      return {
        title: dynamicProgram.title || 'Parents Learning Community',
        subtitle: dynamicProgram.subtitle || 'Growing Together Through Shared Learning & Support',
        description: dynamicProgram.description || 'Program details and family learning initiatives from Permata Harapan Secondary.',
        color: dynamicProgram.color || '#7c3aed',
        colorLight: dynamicProgram.colorLight || '#ede9fe',
        icon: dynamicProgram.icon || 'fa-people-group',
        images: dynamicImagesList,
        date: dynamicProgram.date || 'Monthly',
        frequency: dynamicProgram.frequency || 'Every 2nd Saturday',
        content: dynamicProgram.content || dynamicProgram.description || 'Our community programs are designed to empower parents with practical tools, peer support, and active engagement with the school community.',
        features,
        highlights
      };
    }

    // Static fallback
    return STATIC_PALC_DATA[cleanKey] || STATIC_PALC_DATA['support'];
  }, [dynamicProgram, palcId, resolveImage]);

  useEffect(() => {
    if (!palc.images || palc.images.length <= 1) return;
    const intervalId = setInterval(() => {
      setActiveImageSlide((current) => (current + 1) % palc.images.length);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [palc.images]);

  const handleBack = () => {
    if (onNavigate) {
      onNavigate('parents');
    } else {
      window.location.hash = '#parents';
    }
  };

  return (
    <section className="palc-detail-page">
      {/* Hero Section */}
      <section className="palc-detail-hero" style={{ backgroundColor: palc.color }}>
        <div className="palc-detail-hero-pattern" />
        
        {/* Carousel Media */}
        <div className="palc-detail-hero-image">
          <div className="palc-detail-hero-media" aria-hidden="true">
            {palc.images.length > 0 ? (
              palc.images.map((image, index) => (
                <div
                  key={`${palc.title}-${index}-${image}`}
                  className={`palc-detail-slide ${index === activeImageSlide ? 'active' : ''}`}
                >
                  <img src={image} alt={`${palc.title} slide ${index + 1}`} />
                </div>
              ))
            ) : (
              <div className="palc-detail-slide active" style={{ background: 'linear-gradient(135deg, #1e0938 0%, #3d1b78 100%)' }} />
            )}
          </div>
          <div className="palc-detail-hero-overlay" aria-hidden="true" />
        </div>

        {/* Previous / Next slide buttons */}
        {palc.images.length > 1 && (
          <>
            <button
              type="button"
              className="palc-detail-nav-btn palc-detail-nav-prev"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageSlide((prev) => (prev - 1 + palc.images.length) % palc.images.length);
              }}
              style={{
                position: 'absolute',
                left: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 4,
                background: 'rgba(15, 23, 42, 0.45)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              aria-label="Previous slide"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="palc-detail-nav-btn palc-detail-nav-next"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageSlide((prev) => (prev + 1) % palc.images.length);
              }}
              style={{
                position: 'absolute',
                right: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 4,
                background: 'rgba(15, 23, 42, 0.45)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              aria-label="Next slide"
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}

        <div className="palc-detail-hero-content">
          <button
            className="palc-detail-back-btn"
            onClick={handleBack}
            aria-label="Back to parents"
            type="button"
          >
            <ArrowLeft size={16} /> Back to Parents
          </button>

          <div className="palc-detail-hero-icon">
            <i className={`fas ${palc.icon}`} />
          </div>

          <h1 className="palc-detail-hero-title">{palc.title}</h1>
          <p className="palc-detail-hero-subtitle" style={{ whiteSpace: 'pre-line' }}>{palc.subtitle}</p>

          <div className="palc-detail-meta">
            {palc.frequency && (
              <span className="palc-detail-frequency">
                <Clock size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} />
                {palc.frequency}
              </span>
            )}
            {palc.date && (
              <span className="palc-detail-schedule">
                <Calendar size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} />
                {palc.date}
              </span>
            )}
          </div>
        </div>

        {palc.images.length > 1 && (
          <div className="palc-detail-hero-dots">
            {palc.images.map((_, index) => (
              <button
                key={`dot-${index}`}
                type="button"
                className={`palc-detail-dot ${index === activeImageSlide ? 'active' : ''}`}
                onClick={() => setActiveImageSlide(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
            <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px', fontWeight: 600, marginLeft: '6px' }}>
              {activeImageSlide + 1} / {palc.images.length}
            </span>
          </div>
        )}
      </section>

      {/* Main Content */}
      <section className="palc-detail-main">
        {/* About Section */}
        <div className="palc-detail-section">
          <div className="palc-detail-section-header">
            <h2>About This Program</h2>
            <div className="palc-detail-section-line" style={{ backgroundColor: palc.color }} />
          </div>
          <p className="palc-detail-description" style={{ whiteSpace: 'pre-line' }}>{palc.description}</p>
          <div className="palc-detail-highlight-box" style={{ backgroundColor: palc.colorLight, borderLeftColor: palc.color }}>
            <i className={`fas ${palc.icon}`} style={{ color: palc.color }} />
            <p style={{ whiteSpace: 'pre-line' }}>{palc.content}</p>
          </div>
        </div>

        {/* Features Grid ("What You'll Experience") */}
        {palc.features && palc.features.length > 0 && (
          <div className="palc-detail-section">
            <div className="palc-detail-section-header">
              <h2>What You'll Experience</h2>
              <div className="palc-detail-section-line" style={{ backgroundColor: palc.color }} />
            </div>
            <div className="palc-features-grid">
              {palc.features.map((feature, index) => (
                <div key={`feature-${index}`} className="palc-feature-card" style={{ borderTopColor: palc.color }}>
                  <div 
                    className="palc-feature-icon" 
                    style={{ 
                      '--feature-color': palc.color,
                      '--feature-color-dark': palc.color + 'cc'
                    }}
                  >
                    <i className={`fas ${feature.icon || 'fa-users'}`} />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.detail}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Highlights */}
        {palc.highlights && palc.highlights.length > 0 && (
          <div className="palc-detail-section">
            <div className="palc-detail-section-header">
              <h2>Program Highlights</h2>
              <div className="palc-detail-section-line" style={{ backgroundColor: palc.color }} />
            </div>
            <div className="palc-highlights-grid">
              {palc.highlights.map((highlight, index) => (
                <div key={`highlight-${index}`} className="palc-highlight-card" style={{ backgroundColor: palc.colorLight, borderLeftColor: palc.color }}>
                  <div className="palc-highlight-icon" style={{ color: palc.color }}>
                    <CheckCircle2 size={18} />
                  </div>
                  <p>{highlight}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section 
        className="palc-detail-cta" 
        style={{ 
          '--cta-color': palc.color,
          '--cta-color-dark': palc.color + 'cc'
        }}
      >
        <div className="palc-detail-cta-content">
          <h2>Join Our Community</h2>
          <p>Become part of a supportive network of parents committed to student success and community growth.</p>
          <button 
            type="button"
            className="palc-detail-cta-btn" 
            onClick={() => onNavigate && onNavigate('contact-us')}
          >
            Get Involved <i className="fas fa-arrow-right" />
          </button>
        </div>
      </section>
    </section>
  );
}

export default PaLCDetailPage;
